import {
  activeWeekParities,
  type WeekParity,
  type WeekPattern
} from "../shared/weekPatterns";
import { TeacherPlannedLessonsText } from "../shared/TeacherPlannedLessons";

export interface TeacherWorkloadSource {
  mainTeacherId: string;
  assistantTeacherIds: string[];
  minimum: number;
  maximum: number;
  weekPattern: WeekPattern;
}

interface WorkloadRange {
  minimum: number;
  maximum: number;
}

interface WorkloadTotal {
  main: WorkloadRange;
  assistant: WorkloadRange;
}

const emptyTotal = (): WorkloadTotal => ({
  main: { minimum: 0, maximum: 0 },
  assistant: { minimum: 0, maximum: 0 }
});

export function TeacherWorkloadPanel({
  teachers,
  sources,
  selectedTeacherId
}: {
  teachers: Array<{
    id: string;
    name: string;
    planned_lessons?: number | null;
  }>;
  sources: TeacherWorkloadSource[];
  selectedTeacherId?: string;
}): React.JSX.Element {
  const totals = calculateTeacherWorkloads(teachers, sources);
  const orderedTeachers = [...teachers].sort((left, right) => {
    if (left.id === selectedTeacherId) return -1;
    if (right.id === selectedTeacherId) return 1;
    const leftTotal = combinedMaximum(totals.get(left.id));
    const rightTotal = combinedMaximum(totals.get(right.id));
    return rightTotal - leftTotal || left.name.localeCompare(right.name, "zh-CN");
  });
  return (
    <section className="teacher-workloads" aria-label="教师课时">
      <h4>教师课时</h4>
      {orderedTeachers.length === 0 ? (
        <p>暂无教师</p>
      ) : (
        <div
          className="teacher-workload-list"
          tabIndex={0}
          aria-label="教师课时列表"
        >
          {orderedTeachers.map((teacher) => {
            const teacherTotals = totals.get(teacher.id) ?? {
              ODD: emptyTotal(),
              EVEN: emptyTotal()
            };
            const sameEveryWeek = totalsEqual(teacherTotals.ODD, teacherTotals.EVEN);
            return (
              <article
                key={teacher.id}
                className={teacher.id === selectedTeacherId ? "selected" : ""}
              >
                <strong>{teacher.name}</strong>
                {sameEveryWeek ? (
                  <span>{formatTotal(null, teacherTotals.ODD)}</span>
                ) : (
                  <>
                    <span>{formatTotal("单周", teacherTotals.ODD)}</span>
                    <span>{formatTotal("双周", teacherTotals.EVEN)}</span>
                  </>
                )}
                <TeacherPlannedLessonsText value={teacher.planned_lessons} />
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function buildTeacherWorkloadLabels(
  teachers: Array<{ id: string }>,
  sources: TeacherWorkloadSource[]
): Map<string, string> {
  const totals = calculateTeacherWorkloads(teachers, sources);
  return new Map(
    teachers.map((teacher) => {
      const teacherTotals = totals.get(teacher.id) ?? {
        ODD: emptyTotal(),
        EVEN: emptyTotal()
      };
      if (totalsEqual(teacherTotals.ODD, teacherTotals.EVEN)) {
        return [teacher.id, formatTotal(null, teacherTotals.ODD)];
      }
      return [
        teacher.id,
        `${formatTotal("单周", teacherTotals.ODD)}，${formatTotal("双周", teacherTotals.EVEN)}`
      ];
    })
  );
}

function calculateTeacherWorkloads(
  teachers: Array<{ id: string }>,
  sources: TeacherWorkloadSource[]
): Map<string, Record<WeekParity, WorkloadTotal>> {
  const totals = new Map(
    teachers.map((teacher) => [
      teacher.id,
      { ODD: emptyTotal(), EVEN: emptyTotal() }
    ])
  );
  for (const source of sources) {
    for (const parity of activeWeekParities(source.weekPattern)) {
      addRange(totals.get(source.mainTeacherId)?.[parity].main, source);
      for (const teacherId of source.assistantTeacherIds) {
        addRange(totals.get(teacherId)?.[parity].assistant, source);
      }
    }
  }
  return totals;
}

function addRange(
  target: WorkloadRange | undefined,
  source: Pick<TeacherWorkloadSource, "minimum" | "maximum">
): void {
  if (!target) return;
  target.minimum += source.minimum;
  target.maximum += source.maximum;
}

function formatTotal(label: string | null, total: WorkloadTotal): string {
  const combined = combinedRange(total);
  const prefix = label ? `${label}：` : "";
  return `${prefix}总计 ${formatRange(combined)}（主教${formatRange(total.main)}，助教${formatRange(total.assistant)}）`;
}

function combinedRange(total: WorkloadTotal): WorkloadRange {
  return {
    minimum: total.main.minimum + total.assistant.minimum,
    maximum: total.main.maximum + total.assistant.maximum
  };
}

function formatRange(value: WorkloadRange): string {
  return value.minimum === value.maximum
    ? `${value.minimum}节`
    : `${value.minimum}～${value.maximum}节`;
}

function totalsEqual(left: WorkloadTotal, right: WorkloadTotal): boolean {
  return (
    left.main.minimum === right.main.minimum &&
    left.main.maximum === right.main.maximum &&
    left.assistant.minimum === right.assistant.minimum &&
    left.assistant.maximum === right.assistant.maximum
  );
}

function combinedMaximum(
  totals: Record<WeekParity, WorkloadTotal> | undefined
): number {
  if (!totals) return 0;
  return Math.max(
    totals.ODD.main.maximum + totals.ODD.assistant.maximum,
    totals.EVEN.main.maximum + totals.EVEN.assistant.maximum
  );
}
